import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm, useFormState } from "react-hook-form";
import type { z } from "zod";
import { EffectiveDateConfirmDialog } from "@/components/persons/EffectiveDateConfirmDialog";
import { PersonAvatarEditable } from "@/components/persons/PersonAvatarEditable";
import { ContactSection } from "@/components/persons/person-form/ContactSection";
import { EmploymentSection } from "@/components/persons/person-form/EmploymentSection";
import { NotesSection } from "@/components/persons/person-form/NotesSection";
import { RoleOverviewSection } from "@/components/persons/person-form/RoleOverviewSection";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { type PersonDetail, useCreatePerson } from "@/hooks/use-persons";
import {
  PERSON_STATUSES,
  type PersonCreateInput,
  type PersonUpdateInput,
  personCreateSchema,
} from "@/schemas/persons";

/**
 * Add or edit a person.
 *
 * The shell owns the form, the submit path and the dialog; the four field
 * groups live beside it in `person-form/`. They share nothing but the form
 * handle, which is why splitting on those seams costs nothing — this file was
 * 898 lines, of which the Employment group alone was ~380.
 */

// Status arrives as a bare `string` from PersonDetail; narrow it to the schema
// enum by lookup rather than asserting with `as`, falling back to the create
// default when a DB value is somehow off-enum. Employment type needs no such
// narrowing any more — it is admin-managed config, so the schema types it as a
// plain string and the server checks it against the table.
function toStatus(value: string | null | undefined): PersonCreateInput["status"] {
  return PERSON_STATUSES.find((s) => s === value) ?? "active";
}

interface PersonFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  person?: PersonDetail;
}

export function PersonFormDialog({ open, onOpenChange, person }: PersonFormDialogProps) {
  const isEdit = Boolean(person);
  // First token of the name for the dialog title ("Update Chris's Profile").
  const firstName = person?.name.trim().split(/\s+/)[0] ?? "";
  const createPerson = useCreatePerson();
  // Edits don't save immediately — they go through the effective-date confirm
  // step, which schedules the change. We stash the would-be payload here.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<PersonUpdateInput | null>(null);

  // Three generics, not one: the schema has `.default()` fields, so its INPUT
  // type has them optional while its OUTPUT type has them required. Passing
  // only the output type makes the resolver's own input type unassignable.
  const form = useForm<z.input<typeof personCreateSchema>, unknown, PersonCreateInput>({
    resolver: zodResolver(personCreateSchema),
    defaultValues: {
      name: person?.name ?? "",
      title: person?.title ?? "",
      email: person?.email ?? "",
      personalEmail: person?.personalEmail ?? "",
      phone: person?.phone ?? "",
      entityName: person?.entityName ?? "",
      employmentType: person?.employmentType ?? "full_time",
      status: toStatus(person?.status),
      location: person?.location ?? null,
      roleClassification: person?.roleClassification ?? null,
      hoursPerWeek: person?.hoursPerWeek ?? null,
      workingHoursStart: person?.workingHoursStart ?? "",
      workingHoursEnd: person?.workingHoursEnd ?? "",
      workingHoursStart2: person?.workingHoursStart2 ?? "",
      workingHoursEnd2: person?.workingHoursEnd2 ?? "",
      workingHoursNote: person?.workingHoursNote ?? "",
      startDate: person?.startDate ?? "",
      rehireDate: person?.rehireDate ?? "",
      vcStartDate: person?.vcStartDate ?? "",
      employeeProfileId: person?.employeeProfileId ?? null,
      endDate: person?.endDate ?? "",
      notes: person?.notes ?? "",
      userId: person?.userId ?? null,
      departmentId: person?.departmentId ?? null,
      roleTeamId: person?.roleTeamId ?? null,
      managerId: person?.managerId ?? null,
      dottedLineId: person?.dottedLineId ?? null,
    },
  });

  // Subscribed during render so it's always current — reading formState.isDirty
  // only inside the submit handler leaves RHF's proxy unarmed and it reads a
  // stale false, silently closing the dialog without saving.
  const { isDirty } = useFormState({ control: form.control });

  function onSubmit(values: PersonCreateInput) {
    // Empty optional strings become undefined → stored as NULL.
    const payload: PersonCreateInput = {
      ...values,
      title: values.title?.trim() ? values.title : undefined,
      personalEmail: values.personalEmail?.trim() ? values.personalEmail : undefined,
      phone: values.phone?.trim() ? values.phone : undefined,
      entityName: values.entityName?.trim() ? values.entityName : undefined,
      notes: values.notes?.trim() ? values.notes : undefined,
      // Empty time inputs become NULL (not "don't change").
      workingHoursStart: values.workingHoursStart || null,
      workingHoursEnd: values.workingHoursEnd || null,
      workingHoursStart2: values.workingHoursStart2 || null,
      workingHoursEnd2: values.workingHoursEnd2 || null,
      workingHoursNote: values.workingHoursNote?.trim() ? values.workingHoursNote : undefined,
    };

    if (isEdit) {
      // Photo changes apply immediately (PersonAvatarEditable) and never dirty
      // the form — with no other edits there's nothing to review, just close.
      // handleOpenChange (not onOpenChange) so any leftover form state is
      // discarded on this path too.
      if (!isDirty) {
        handleOpenChange(false);
        return;
      }
      // Defer to the effective-date confirm step instead of saving now.
      setPendingPayload(payload);
      setConfirmOpen(true);
      return;
    }

    createPerson.mutate(payload, {
      onSuccess: () => {
        form.reset();
        onOpenChange(false);
      },
    });
  }

  const isPending = createPerson.isPending;

  // Closing without saving discards the in-progress edits. Without this, a
  // dirty form (e.g. a status flip whose review step was canceled) survives
  // the close and reopens showing values that contradict the profile. The
  // split-shift toggle needs no reset here — it lives inside the dialog's
  // content, which Radix unmounts on close.
  function handleOpenChange(next: boolean) {
    if (!next) form.reset();
    onOpenChange(next);
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          aria-describedby={undefined}
          className="sm:max-w-5xl max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle>
              {isEdit && person ? `Update ${firstName}'s Profile` : "Add person"}
            </DialogTitle>
            <DialogDescription>
              {isEdit && person
                ? "Change this person's profile, role and reporting line."
                : "Add someone to the directory, with their role and reporting line."}
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Identity header: photo + name. Job title lives in the
                  Employment section below, mirroring the profile page. */}
              <div className="flex items-start gap-5">
                {isEdit && person && (
                  <div className="pt-6">
                    <PersonAvatarEditable person={person} />
                  </div>
                )}
                <div className="flex-1">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Full name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <ContactSection form={form} />
              <RoleOverviewSection form={form} personId={person?.id} />
              <EmploymentSection
                form={form}
                hasSecondShift={Boolean(person?.workingHoursStart2 || person?.workingHoursEnd2)}
              />
              <NotesSection form={form} />

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending ? "Saving…" : isEdit ? "Review changes" : "Add person"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      {isEdit && person && (
        <EffectiveDateConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          personId={person.id}
          updates={pendingPayload}
          onScheduled={() => {
            form.reset();
            onOpenChange(false);
          }}
        />
      )}
    </>
  );
}
